import { Download, RefreshCw, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLightboxStore, toast } from "@/stores/ui";
import { useGeneratedAssetsQuery } from "@/lib/queries";

export function WorksView() {
  const showLightbox = useLightboxStore((s) => s.show);

  const { items: assets, isLoading, isError, refetch, isFetching } =
    useGeneratedAssetsQuery();

  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
        <div>
          <h3 className="text-lg font-semibold text-foreground">我的作品</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            点击图片放大，按「下载原图」保存到本地。
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            await refetch();
            toast("已刷新");
          }}
          disabled={isFetching}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          刷新
        </Button>
      </div>

      <div className="px-6 py-5">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            加载中…
          </div>
        ) : isError ? (
          <div className="flex h-48 items-center justify-center text-sm text-destructive">
            加载失败，请刷新重试。
          </div>
        ) : assets.length === 0 ? (
          <EmptyWorks />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {assets.map((a) => (
              <article
                key={a.asset_id}
                className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-lg"
              >
                <button
                  type="button"
                  className="relative aspect-square overflow-hidden bg-muted"
                  onClick={() => showLightbox(a.file_url)}
                  aria-label="点击放大查看"
                >
                  <img
                    src={a.thumbnail_url || a.file_url}
                    alt={a.name || "生成图"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
                    onError={(e) => {
                      const card = (e.currentTarget as HTMLElement).closest("article");
                      if (card) (card as HTMLElement).style.display = "none";
                    }}
                  />
                </button>
                <div className="flex items-center gap-2 border-t border-border bg-secondary px-3 py-2">
                  <a
                    href={a.file_url}
                    download={`${a.asset_id}.png`}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground transition-colors hover:bg-accent/90"
                  >
                    <Download className="h-3.5 w-3.5" />
                    下载原图
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyWorks() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="rounded-full border border-border bg-card p-4 text-muted-foreground">
        <ImageOff className="h-7 w-7" />
      </div>
      <p className="text-base font-medium text-foreground">还没有生成任何图片</p>
      <p className="text-sm text-muted-foreground">
        去「自由创作」生成第一张作品。
      </p>
    </div>
  );
}
